import { useEffect, useState } from "react";
import {
  Calendar,
  Pencil,
  GraduationCap,
  Star,
  Clock,
  BookOpen,
  ArrowRight
} from "lucide-react";
import { useTutorAuth } from "../../context/tutorAuthContext";
import axios from "axios";
import { Link } from "react-router-dom";

/* ---------- CUSTOM NAIRA ICON ---------- */
function NairaIcon({ size = 24, className }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M6 18V6l12 12V6" />
      <line x1="4" y1="10" x2="20" y2="10" />
      <line x1="4" y1="14" x2="20" y2="14" />
    </svg>
  );
}

export default function DashboardHeader() {
  const { tutor } = useTutorAuth();
  const [time, setTime] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState([]);
  const [upcomingSessions, setUpcomingSessions] = useState([]);

  const BASE_URL = process.env.REACT_APP_BASE_URL;

  const getInitials = (name) => {
    if (!name) return "T";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  useEffect(() => {
    if (!tutor?.id) return;

    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        const res = await axios.get(
          `${BASE_URL}/api/tutors/dashboard-stats/${tutor.id}`
        );

        const data = res.data;

        setStats([
          {
            label: "Students",
            value: data.totalStudents || 0,
            icon: GraduationCap,
            color: "text-indigo-600",
            bg: "bg-indigo-50"
          },
          {
            label: "Courses",
            value: data.totalCourses || 0,
            icon: BookOpen,
            color: "text-purple-600",
            bg: "bg-purple-50"
          },
          {
            label: "Earnings",
            value: `₦${Number(data.totalEarnings || 0).toLocaleString()}`,
            icon: NairaIcon,
            color: "text-emerald-600",
            bg: "bg-emerald-50"
          },
          {
            label: "Rating",
            value: `${Number(data.rating || 0).toFixed(1)}/5`,
            icon: Star,
            color: "text-amber-500",
            bg: "bg-amber-50"
          }
        ]);

        setUpcomingSessions(data.upcomingSessions || []);
      } catch (error) {
        console.error("Dashboard stats error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [tutor?.id, BASE_URL]);

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  if (!tutor && loading)
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );

  return (
    <div className="w-full max-w-7xl mx-auto px-4 md:px-8 py-6 space-y-8 overflow-x-hidden">

      {/* HEADER */}
      <div className="relative overflow-hidden rounded-[2.5rem] border border-white/40 bg-white/40 p-6 md:p-12 shadow-2xl shadow-blue-900/5 backdrop-blur-3xl">

        <div className="absolute -top-24 -left-24 w-96 h-96 bg-blue-500/10 rounded-full blur-[100px] animate-pulse"></div>
        <div className="absolute -bottom-24 -right-24 w-80 h-80 bg-indigo-500/10 rounded-full blur-[80px] animate-pulse delay-700"></div>

        <div className="relative z-10 flex flex-col-reverse md:flex-row items-center justify-between gap-10">

          <div className="flex flex-col space-y-5 w-full md:w-auto text-center md:text-left">

            <div>
              <span className="inline-block px-4 py-1.5 rounded-full bg-blue-600/10 border border-blue-200 text-[11px] font-black text-blue-700 uppercase tracking-[0.2em] mb-4">
                Tutor Overview
              </span>

              <h1 className="text-3xl sm:text-4xl md:text-6xl font-black text-slate-900 tracking-tight leading-none">
                Welcome, <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                  {tutor?.name?.split(" ")[0] || "Tutor"}
                </span>
              </h1>
            </div>

            <div className="flex flex-wrap justify-center md:justify-start items-center gap-4">

              <div className="flex items-center gap-2 text-xs font-black text-emerald-700 bg-emerald-50 px-5 py-2.5 rounded-2xl border border-emerald-100 shadow-sm">
                <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse"></span>
                LIVE STATUS
              </div>

              <div className="flex items-center gap-3 text-xs font-black text-slate-700 bg-white/60 px-5 py-2.5 rounded-2xl border border-white/80 shadow-sm">
                <Clock size={16} className="text-blue-600" />
                {time.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit"
                })}
              </div>

            </div>
          </div>

          {/* AVATAR */}
          <div className="relative flex-shrink-0">

            <div className="absolute -left-6 top-4 z-20 animate-bounce">
              <Link
                to="/tutor/schedule"
                className="flex h-12 w-12 items-center justify-center rounded-2xl border-2 border-white bg-white/80 shadow-xl text-blue-600 hover:scale-110 transition-all"
              >
                <Calendar size={22} />
              </Link>
            </div>

            <div className="relative h-32 w-32 md:h-48 md:w-48 rounded-[3.5rem] bg-gradient-to-br from-blue-600 to-indigo-700 p-1 shadow-2xl ring-8 ring-blue-50/50">

              <div className="h-full w-full rounded-[3.2rem] bg-white flex items-center justify-center overflow-hidden">

                {tutor?.profile_img ? (
                  <img
                    src={`${BASE_URL}/uploads/${tutor.profile_img}`}
                    alt="Profile"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <span className="text-5xl md:text-6xl font-black text-blue-600 tracking-tighter">
                    {getInitials(tutor?.name)}
                  </span>
                )}

              </div>
            </div>

            <div className="absolute -right-4 bottom-4 z-20 animate-bounce delay-300">
              <Link
                to="/tutor/profile-settings"
                className="flex h-12 w-12 items-center justify-center rounded-2xl border-2 border-white bg-white/80 shadow-xl text-indigo-600 hover:scale-110 transition-all"
              >
                <Pencil size={20} />
              </Link>
            </div>

          </div>
        </div>
      </div>

      {/* STATS */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">

        {stats.map((item, idx) => {
          const Icon = item.icon;

          return (
            <div
              key={idx}
              className="group relative overflow-hidden rounded-[2.5rem] border border-white bg-white/60 p-5 md:p-8 transition-all duration-300 hover:shadow-2xl hover:shadow-blue-500/10 hover:-translate-y-2"
            >
              <div
                className={`h-14 w-14 md:h-16 md:w-16 ${item.bg} rounded-[1.5rem] flex items-center justify-center mb-6 transition-transform group-hover:rotate-12`}
              >
                <Icon size={32} className={item.color} />
              </div>

              <span className="text-xs font-black text-slate-400 uppercase tracking-widest block mb-1">
                {item.label}
              </span>

              <span className="text-2xl md:text-4xl font-black text-slate-900 leading-none">
                {item.value}
              </span>
            </div>
          );
        })}

      </div>

      {/* UPCOMING */}
      <div className="pt-4">

        <div className="flex items-center justify-between mb-6 px-2">
          <h2 className="text-2xl font-black text-slate-900 flex items-center gap-3">
            Next Sessions
            <span className="h-2 w-2 rounded-full bg-blue-600"></span>
          </h2>

          <Link
            to="/tutor/schedule"
            className="group flex items-center gap-2 text-sm font-black text-blue-600 hover:gap-3 transition-all"
          >
            VIEW ALL <ArrowRight size={16} />
          </Link>
        </div>

        {upcomingSessions.length === 0 ? (
          <div className="bg-white/40 rounded-[2rem] p-12 text-center border-2 border-dashed border-slate-200">
            <Calendar className="mx-auto h-12 w-12 text-slate-300 mb-4" />
            <p className="text-slate-500 font-bold">
              Your schedule is clear for today!
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            {upcomingSessions.map((session, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between bg-white/60 p-6 rounded-[2rem] border border-white hover:border-blue-200 transition-all shadow-sm flex-wrap gap-4"
              >
                <div className="flex items-center gap-5">

                  <div className="h-12 w-12 rounded-2xl bg-blue-600 flex items-center justify-center text-white font-black">
                    {session.student?.[0] || "S"}
                  </div>

                  <div>
                    <p className="font-black text-slate-800 text-lg leading-tight">
                      {session.course}
                    </p>
                    <p className="text-sm font-bold text-slate-500">
                      {session.student}
                    </p>
                  </div>

                </div>

                <div className="text-right">
                  <p className="text-sm font-black text-blue-600">
                    {session.time}
                  </p>
                  <p className="text-[10px] font-bold text-slate-400 uppercase">
                    {new Date(session.date).toDateString()}
                  </p>
                </div>

              </div>
            ))}

          </div>
        )}
      </div>

    </div>
  );
}